import { WAWI_BESTAND_FILTER, WAWI_PRUEFLISTEN, WAWI_SHOP_FILIALE, WAWI_SHOP_STATUS, WAWI_SORTIERUNG, type Me, type ModulFreischaltung, type WawiBestandFilter, type WawiFilialBestand, type WawiPruefliste, type WawiShopStatus, type WawiSortierung } from '@portal/contracts';

/**
 * Warenwirtschaft (#863): reine Hilfsfunktionen der Team-Seite (Routen, Filter, Anzeige, Rechte) — getestet ohne DOM.
 * Modul-Schlüssel/Pfad bleiben `lager` / `/lager` (#820). Rechte prüft immer die API; die Oberfläche blendet nur aus.
 */
export const WAWI_PFAD = '/lager';

export type WawiRoute =
  | { seite: 'artikel' }
  | { seite: 'detail'; nr: number }
  | { seite: 'warengruppen' }
  | { seite: 'sync' }
  | { seite: 'unbekannt' };

export const UNTERNAVIGATION = [
  { seite: 'artikel', label: 'Artikel', pfad: WAWI_PFAD },
  { seite: 'warengruppen', label: 'Warengruppen', pfad: `${WAWI_PFAD}/warengruppen` },
  { seite: 'sync', label: 'Sync', pfad: `${WAWI_PFAD}/sync` },
] as const;

export function wawiRoute(pathname: string): WawiRoute {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === WAWI_PFAD) return { seite: 'artikel' };
  if (p === `${WAWI_PFAD}/warengruppen`) return { seite: 'warengruppen' };
  if (p === `${WAWI_PFAD}/sync`) return { seite: 'sync' };
  const m = /^\/lager\/artikel\/([1-9]\d{0,9})$/.exec(p);
  if (m) return { seite: 'detail', nr: Number(m[1]) };
  return { seite: 'unbekannt' };
}

export function aktiverReiter(r: WawiRoute): 'artikel' | 'warengruppen' | 'sync' | null {
  if (r.seite === 'detail') return 'artikel';
  return r.seite === 'unbekannt' ? null : r.seite;
}

export const artikelPfad = (nr: number) => `${WAWI_PFAD}/artikel/${nr}`;

type MitModulen = Pick<Me, 'modules'> | { modules?: ModulFreischaltung };
export const darfVerwalten = (me: MitModulen): boolean => me.modules?.lager === 'verwalten';

export interface WawiFilter {
  suche: string;
  filiale: string;
  ober: string;
  wg: string;
  /** „Nicht zugeordnet“ (Warengruppe IS NULL) — Query `zuordnung=ohne`, schließt ober/wg aus (Review 221) */
  ohneZuordnung: boolean;
  bestand: WawiBestandFilter;
  /** #899b */
  marke: string;
  status: string;
  shop: WawiShopStatus | '';
  pruefliste: WawiPruefliste | '';
  sort: WawiSortierung;
  ab: boolean;
  seite: number;
}

/** Filter aus der URL-Query (Teilen/Zurück); Unbekanntes fällt auf den Standard „Alle“ zurück (#899b komplettes Sortiment) */
export function filterAusQuery(search: string): WawiFilter {
  const q = new URLSearchParams(search);
  const b = q.get('bestand') ?? '';
  const seite = Number(q.get('seite') ?? 1);
  const ohneZuordnung = q.get('zuordnung') === 'ohne';
  return {
    suche: q.get('suche') ?? '',
    filiale: /^\d{1,5}$/.test(q.get('filiale') ?? '') ? q.get('filiale')! : '',
    ober: ohneZuordnung ? '' : q.get('ober') ?? '',
    wg: ohneZuordnung ? '' : q.get('wg') ?? '',
    ohneZuordnung,
    bestand: (WAWI_BESTAND_FILTER as readonly string[]).includes(b) ? (b as WawiBestandFilter) : 'alle',
    marke: (q.get('marke') ?? '').slice(0, 100),
    status: (q.get('status') ?? '').slice(0, 100),
    shop: (WAWI_SHOP_STATUS as readonly string[]).includes(q.get('shop') ?? '') ? (q.get('shop') as WawiShopStatus) : '',
    pruefliste: (WAWI_PRUEFLISTEN as readonly string[]).includes(q.get('pruefliste') ?? '') ? (q.get('pruefliste') as WawiPruefliste) : '',
    sort: (WAWI_SORTIERUNG as readonly string[]).includes(q.get('sort') ?? '') ? (q.get('sort') as WawiSortierung) : 'nr',
    ab: q.get('richtung') === 'ab',
    seite: Number.isInteger(seite) && seite > 0 ? seite : 1,
  };
}

export function filterZuQuery(f: WawiFilter): string {
  const q = new URLSearchParams();
  if (f.suche.trim()) q.set('suche', f.suche.trim());
  if (f.filiale) q.set('filiale', f.filiale);
  if (f.ohneZuordnung) q.set('zuordnung', 'ohne');
  else {
    if (f.ober) q.set('ober', f.ober);
    if (f.wg) q.set('wg', f.wg);
  }
  if (f.bestand !== 'alle') q.set('bestand', f.bestand);
  if (f.marke) q.set('marke', f.marke);
  if (f.status) q.set('status', f.status);
  if (f.shop) q.set('shop', f.shop);
  if (f.pruefliste) q.set('pruefliste', f.pruefliste);
  if (f.sort !== 'nr') q.set('sort', f.sort);
  if (f.ab) q.set('richtung', 'ab');
  if (f.seite > 1) q.set('seite', String(f.seite));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const BESTAND_LABEL: Record<WawiBestandFilter, string> = {
  alle: 'Alle (Sortiment)',
  gt0: `Bestand F${WAWI_SHOP_FILIALE} > 0`,
  lt0: `Bestand F${WAWI_SHOP_FILIALE} < 0`,
  null: `Bestand F${WAWI_SHOP_FILIALE} = 0`,
  ungleich0: 'Bestand ≠ 0 (irgendwo)',
  kein: 'Kein Bestand',
  negativ: 'Minusbestand (irgendwo)',
};
export const SHOP_LABEL: Record<WawiShopStatus, string> = { aktiv: 'aktiv', entwurf: 'Entwurf', archiviert: 'archiviert', fehlt: 'nicht im Shop' };
export const SORT_LABEL: Record<WawiSortierung, string> = {
  nr: 'Artikel-Nr', bezeichnung: 'Bezeichnung', marke: 'Marke', bestand: `Bestand F${WAWI_SHOP_FILIALE}`, shop: 'Shopify-Bestand', abweichung: 'Abweichung', geaendert: 'Letzte Änderung',
};

/** #899b Spalten = Filialen mit realem Bestand laut API (dynamisch, keine festen 1/2/3 mehr) */
export function filialSpalten(filialen: readonly number[]): number[] {
  return [...new Set(filialen)].sort((a, b) => a - b);
}

/** Abweichung NEO − Shopify: „—“ wenn eine Seite fehlt, sonst mit Vorzeichen */
export function abweichungText(n: number | null): { text: string; auffaellig: boolean } {
  if (n === null) return { text: '—', auffaellig: false };
  return { text: n > 0 ? `+${zahl(n)}` : zahl(n), auffaellig: n !== 0 };
}

/** CSV-Export-Pfad mit denselben Filtern (ohne Seite) */
export function exportPfad(api: string, f: WawiFilter): string {
  const q = new URLSearchParams(Object.entries(artikelQuery({ ...f, seite: 1 })).filter(([k, v]) => v !== undefined && k !== 'seite' && k !== 'groesse').map(([k, v]) => [k, String(v)]));
  const s = q.toString();
  return `${api}/artikel.csv${s ? `?${s}` : ''}`;
}

export const filialLabel = (nr: number) => (nr === 999 ? 'Filiale 999 (Sonder)' : `Filiale ${nr}`);
export const filialKurz = (nr: number) => (nr === 999 ? '999*' : `F${nr}`);

/** Bestand einer Filiale: Zahl bzw. „—“ ohne Datensatz; Minus mit echtem Minuszeichen */
export function bestandIn(bestaende: readonly WawiFilialBestand[], filiale: number): { text: string; negativ: boolean } {
  const b = bestaende.find((x) => x.filialNr === filiale);
  if (!b) return { text: '—', negativ: false };
  return { text: zahl(b.bestand), negativ: b.bestand < 0 };
}

const ZAHL = new Intl.NumberFormat('de-DE');
export const zahl = (n: number) => (n < 0 ? `−${ZAHL.format(-n)}` : ZAHL.format(n));

/** Warengruppe als „Ober › Gruppe“ bzw. „Nicht zugeordnet“ */
export function warengruppeAnzeige(a: { oberName: string | null; oberNr: string | null; wgName: string | null; wgNr: string | null }): string {
  if (!a.oberNr) return 'Nicht zugeordnet';
  return `${a.oberName ?? a.oberNr} › ${a.wgName ?? a.wgNr ?? ''}`;
}

/** Ziel eines Klicks im Warengruppen-Baum: Artikelliste gefiltert auf die Gruppe; null-Gruppe → `zuordnung=ohne` (Review 221 P2) */
export function warengruppeFilterPfad(oberNr: string | null, wgNr: string | null): string {
  const basis: WawiFilter = { suche: '', filiale: '', ober: '', wg: '', ohneZuordnung: false, bestand: 'alle', marke: '', status: '', shop: '', pruefliste: '', sort: 'nr', ab: false, seite: 1 };
  const f: WawiFilter = oberNr === null ? { ...basis, ohneZuordnung: true } : { ...basis, ober: oberNr, wg: wgNr ?? '' };
  return `${WAWI_PFAD}${filterZuQuery(f)}`;
}

/** Parameter der Artikel-API aus dem UI-Filter (nur gesetzte Werte) */
export function artikelQuery(f: WawiFilter, groesse = 50): Record<string, string | number | undefined> {
  return {
    suche: f.suche || undefined, filiale: f.filiale || undefined, bestand: f.bestand, seite: f.seite, groesse,
    marke: f.marke || undefined, status: f.status || undefined, shop: f.shop || undefined, pruefliste: f.pruefliste || undefined,
    sort: f.sort !== 'nr' ? f.sort : undefined, richtung: f.ab ? 'ab' : undefined,
    ...(f.ohneZuordnung ? { zuordnung: 'ohne' } : { ober: f.ober || undefined, wg: f.wg || undefined }),
  };
}

export const PHASE_LABEL = { bereit: 'bereit', laeuft: 'Lauf aktiv', unvollstaendig: 'Unvollständig – Snapshot nicht veröffentlicht', fehler: 'Fehler' } as const;
